import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { UserAuthMiddleware } from './middleware';
import { Schema } from 'effect';
import { BadRequestError, InternalServerError } from './errors';

/**
 * Request to get a presigned upload URL for a document.
 */
const GetUploadUrlRequest = Schema.Struct({
  filename: Schema.String.annotate({
    description:
      'The filename with extension (e.g., "my-document.pdf"). Extension determines Content-Type.',
  }),
});

/**
 * Response containing the presigned upload URL and form fields.
 *
 * The client should POST to `signed_upload_url` with the `fields` as form data,
 * plus the file as the last field named "file".
 */
const GetUploadUrlResponse = Schema.Struct({
  status: Schema.Literal('success'),
  signed_upload_url: Schema.String.annotate({
    description: 'The presigned URL to POST the file to',
  }),
  fields: Schema.StructWithRest(
    Schema.Struct({
      key: Schema.String.annotate({
        description: 'The S3 object key (path) where the file will be stored',
      }),
      'Content-Type': Schema.String.annotate({
        description: 'MIME type of the file',
      }),
      'Content-Disposition': Schema.String.annotate({
        description: 'Attachment disposition with original filename',
      }),
    }),
    // AWS presigned POST fields - these vary by AWS config
    [Schema.Record(Schema.String, Schema.String)],
  ),
});

/**
 * Get a presigned URL for uploading a document to S3.
 *
 * This endpoint originally only supported PDFs, but now supports all convertible file types.
 * After uploading, use the S3 URL with CreateAudio to start conversion.
 *
 * @see Python impl: submodules/backend/apps/api/listening/convert/convert_routes.py
 */
const getUploadUrlEndpoint = HttpApiEndpoint.post(
  'GetUploadUrl',
  '/get-upload-url',
  {
    payload: GetUploadUrlRequest,
    success: GetUploadUrlResponse.pipe(HttpApiSchema.status(200)),
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get Upload URL')
  .annotate(
    OpenApi.Description,
    'Get a presigned URL for uploading a document to S3. After uploading, use the S3 URL with CreateAudio to start conversion.',
  );

export const pdfGroup = HttpApiGroup.make('Pdf')
  .add(getUploadUrlEndpoint)
  .prefix('/pdf');

// Also export the schemas for use in tests and scripts
